import { useEffect, useMemo, useState } from 'react';
import { Check, LoaderCircle, SwatchBook, X } from 'lucide-react';
import { hexToRgb, MARD_291 } from '../core/palette';
import type { ContentCenterEntry, PatternProject } from '../types';

interface PaletteChoice {
  id: string;
  name: string;
  brand: string;
  colors: Array<{ code: string; name: string; hex: string }>;
}

interface Props {
  project: PatternProject;
  onClose: () => void;
  onConfirm: (value: { brand: string; name: string; codeMap?: Record<string, string> }) => void;
}

function colorDistance(leftHex: string, rightHex: string) {
  const left = hexToRgb(leftHex);
  const right = hexToRgb(rightHex);
  return Math.hypot(left.r - right.r, left.g - right.g, left.b - right.b);
}

function normalizedColors(entry: ContentCenterEntry) {
  return (entry.colors || []).filter((color) => /^#[0-9a-f]{6}$/i.test(color.hex) && color.code.trim());
}

export function PaletteConversionModal({ project, onClose, onConfirm }: Props) {
  const [entries, setEntries] = useState<ContentCenterEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(project.paletteBrand && project.paletteBrand !== 'MARD' ? '' : 'mard-291');

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/content-center?kind=palette', { cache: 'no-store', signal: controller.signal })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || '色卡读取失败');
        const next = Array.isArray(data.entries) ? data.entries : [];
        setEntries(next);
        const current = next.find((entry: ContentCenterEntry) => String(entry.metadata?.brand || '').trim() === project.paletteBrand);
        if (current) setSelectedId(current.id);
      })
      .catch((reason) => {
        if (reason.name !== 'AbortError') setError(reason.message || '色卡读取失败');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [project.paletteBrand]);

  const choices = useMemo<PaletteChoice[]>(() => [
    { id: 'mard-291', name: 'MARD 291 标准色卡', brand: 'MARD', colors: MARD_291 },
    ...entries
      .filter((entry) => entry.slug !== 'mard-291' && normalizedColors(entry).length)
      .map((entry) => ({
        id: entry.id,
        name: entry.title,
        brand: String(entry.metadata?.brand || entry.title).trim().slice(0, 16) || '合作品牌',
        colors: normalizedColors(entry)
      }))
  ], [entries]);

  const usedIndexes = useMemo(() => {
    const values = new Set<number>();
    for (let index = 0; index < project.colorIndices.length; index += 1) {
      if (!project.empty[index]) values.add(project.colorIndices[index]);
    }
    return [...values].sort((left, right) => MARD_291[left].code.localeCompare(MARD_291[right].code, 'en', { numeric: true }));
  }, [project]);

  const selected = choices.find((choice) => choice.id === selectedId) || choices[0];
  const mappings = useMemo(() => usedIndexes.map((sourceIndex) => {
    const source = MARD_291[sourceIndex];
    if (selected.brand === 'MARD') return { sourceIndex, source, target: source, distance: 0 };
    const target = selected.colors.reduce((best, candidate) =>
      colorDistance(source.hex, candidate.hex) < colorDistance(source.hex, best.hex) ? candidate : best, selected.colors[0]);
    return { sourceIndex, source, target, distance: colorDistance(source.hex, target.hex) };
  }), [selected, usedIndexes]);

  function confirm() {
    if (selected.brand === 'MARD') {
      onConfirm({ brand: 'MARD', name: 'MARD 291 标准色卡' });
      return;
    }
    onConfirm({
      brand: selected.brand,
      name: selected.name,
      codeMap: Object.fromEntries(mappings.map((item) => [String(item.sourceIndex), item.target.code]))
    });
  }

  return (
    <div className="modal-backdrop palette-conversion-backdrop" role="presentation">
      <section className="modal-card palette-conversion-modal" role="dialog" aria-modal="true" aria-labelledby="palette-conversion-title">
        <header className="palette-conversion-head">
          <div className="palette-conversion-title">
            <span className="palette-conversion-emblem"><SwatchBook size={22} /></span>
            <div><span className="modal-eyebrow">PALETTE CONVERSION</span><h2 id="palette-conversion-title">转换色卡品牌</h2><p>保留图纸颜色，仅把施工色号映射到目标品牌。</p></div>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="关闭转换色卡"><X size={20} /></button>
        </header>
        {loading ? <div className="palette-conversion-state"><LoaderCircle className="spin" size={23} />正在读取色卡中心…</div>
          : error ? <div className="palette-conversion-state error">{error}</div>
            : <>
              <div className="palette-brand-grid" role="list" aria-label="可转换色卡品牌">
                {choices.map((choice) => <button key={choice.id} className={selected.id === choice.id ? 'active' : ''} type="button" onClick={() => setSelectedId(choice.id)}>
                  <span className="palette-brand-icon"><SwatchBook size={19} /></span>
                  <span><strong>{choice.brand}</strong><small>{choice.name} · {choice.colors.length} 色</small></span>
                  {selected.id === choice.id && <Check size={18} />}
                </button>)}
              </div>
              <div className="palette-mapping-head">
                <div><strong>{project.paletteBrand || 'MARD'} → {selected.brand}</strong><span>当前图纸使用 {usedIndexes.length} 种颜色</span></div>
                <small><Check size={14} />格子、RGB 颜色和施工进度均保持不变</small>
              </div>
              <div className="palette-mapping-columns" aria-hidden="true"><span>当前色号</span><span>目标色号</span><span>匹配度</span></div>
              <div className="palette-mapping-list">
                {mappings.map((item) => <div className="palette-mapping-row" key={item.sourceIndex}>
                  <span className="palette-mapping-color"><span className="mapping-swatch" style={{ background: item.source.hex }} /><span><small>MARD</small><strong>{item.source.code}</strong></span></span>
                  <i className="palette-mapping-arrow">→</i>
                  <span className="palette-mapping-color"><span className="mapping-swatch" style={{ background: item.target.hex }} /><span><small>{selected.brand}</small><strong>{item.target.code}</strong></span></span>
                  <em>{Math.round(Math.max(0, 100 - item.distance / 4.42))}%</em>
                </div>)}
              </div>
              <footer className="palette-conversion-actions">
                <button type="button" onClick={onClose}>取消</button>
                <button className="primary" type="button" onClick={confirm}><Check size={17} />应用品牌色号</button>
              </footer>
            </>}
      </section>
    </div>
  );
}
